import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

const TOKEN_KEY = 'rc_access';
const REFRESH_KEY = 'rc_refresh';
const USER_KEY = 'rc_user';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem(USER_KEY)); } catch { return null; }
  });
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(false);

  const persistSession = useCallback((session, profile) => {
    localStorage.setItem(TOKEN_KEY, session.access);
    if (session.refresh) localStorage.setItem(REFRESH_KEY, session.refresh);
    if (profile) {
      const normalized = Array.isArray(profile) ? profile[0] : profile;
      localStorage.setItem(USER_KEY, JSON.stringify(normalized));
      setUser(normalized);
    }
    setToken(session.access);
  }, []);

  const fetchProfile = useCallback(async (accessToken) => {
    try {
      const res = await fetch('https://backproject.meanc.net/meanc/users/me/profile-show/', {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!res.ok) return null;
      const data = await res.json();
      const profile = Array.isArray(data) ? data[0] : data;
      localStorage.setItem(USER_KEY, JSON.stringify(profile));
      setUser(profile);
      return profile;
    } catch {
      return null;
    }
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await fetch('https://backproject.meanc.net/meanc/secure-auth/token/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim(), password }),
    });
    const data = await res.json();
    if (!res.ok || !data.access) {
      const detail = Array.isArray(data.detail) ? data.detail[0] : data.detail;
      const msg = data.message || detail ||
        (typeof data === 'object' ? Object.values(data).flat()[0] : null) ||
        'Invalid credentials';
      throw new Error(msg);
    }
    await persistSession(data, null);
    await fetchProfile(data.access);
    return data;
  }, [persistSession, fetchProfile]);

  const confirmEmail = useCallback(async (email, code) => {
    const res = await fetch('https://backproject.meanc.net/meanc/secure-auth/confirm/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim(), code }),
    });
    const data = await res.json();
    if (!res.ok || !data.access) {
      const detail = Array.isArray(data.detail) ? data.detail[0] : data.detail;
      const msg = data.message || detail || 'Confirmation failed';
      throw new Error(msg);
    }
    await persistSession(data, null);
    await fetchProfile(data.access);
    return data;
  }, [persistSession, fetchProfile]);

  const register = useCallback(async (email, password, dateOfBirth) => {
    const res = await fetch('https://backproject.meanc.net/meanc/secure-auth/register/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim(), password, date_of_birth: dateOfBirth }),
    });
    const data = await res.json();
    if (!res.ok) {
      const msg = data?.message || data?.detail || (typeof data === 'object' ? Object.values(data)[0] : null) || 'Registration failed';
      throw new Error(msg);
    }
    return data;
  }, []);

  const loginWithGoogle = useCallback(async (accessToken) => {
    const res = await fetch('https://backproject.meanc.net/meanc/logingoogle/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ access_token: accessToken }),
    });
    const data = await res.json();
    if (!res.ok || !data.access) throw new Error(data.detail || data.error || 'Google sign-in failed');
    await persistSession(data, null);
    await fetchProfile(data.access);
    return data;
  }, [persistSession, fetchProfile]);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    if (!token) return;
    await fetchProfile(token);
  }, [token, fetchProfile]);

  useEffect(() => {
    if (token && !user) {
      fetchProfile(token);
    }
  }, [token, user, fetchProfile]);

  return (
    <AuthContext.Provider value={{ user, token, loading, setLoading, login, register, confirmEmail, loginWithGoogle, logout, refreshUser, fetchProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
